/**
 * Pure-function chart clock for the linear-in-wall-time speed ramp
 * applied to a loop's audio playback. Extracted from `AudioEngine` so
 * the math is unit-testable without a real Web Audio context, and so
 * `getSongTime`/`beatToCtxTime`/`getPlaybackRate` share one definition
 * of the integration instead of three near-duplicate copies.
 *
 * Model: while a loop runs, the audio source's `playbackRate` is
 * linearly interpolated from `startSpeed` to `endSpeed` over a wall
 * duration of `loopWallDuration` seconds, so the rate at wall-time
 * `t ∈ [0, loopWallDuration]` is
 *
 *     r(t) = startSpeed + (endSpeed - startSpeed) * t / loopWallDuration.
 *
 * Lead-in (`t < 0`) plays at constant `startSpeed` because the audio
 * source isn't producing sound yet — the count-in is just SFX. Past
 * the loop boundary (`t > loopWallDuration`) we extrapolate at
 * constant `endSpeed`; this only matters if a caller queries beyond
 * the loop end (the session lifecycle restarts the source at the next
 * loop's `startSpeed`).
 *
 * `loopWallDuration` is set such that `songTimeAt(L_wall) ===
 * chartDurationSec` — i.e. the integrated song-time at the loop's
 * wall end equals the chart's natural duration. The relevant identity
 * for callers is therefore `loopWallDuration = chartDurationSec * 2 /
 * (startSpeed + endSpeed)`.
 */

export interface LoopSpeedClock {
  startSpeed: number;
  endSpeed: number;
  loopWallDuration: number;
}

/**
 * Song-time elapsed for a wall-time offset `t` from the loop's anchor
 * (typically `audioContext.currentTime - songStartTime`). Negative `t`
 * is the lead-in window. Returns `t * startSpeed` when the ramp is
 * flat so callers don't have to special-case loop 1.
 */
export function songTimeAt(clock: LoopSpeedClock, t: number): number {
  const { startSpeed, endSpeed, loopWallDuration } = clock;
  if (t <= 0 || startSpeed === endSpeed) {
    return t * startSpeed;
  }
  if (t >= loopWallDuration) {
    const rampSongTime = (loopWallDuration * (startSpeed + endSpeed)) / 2;
    return rampSongTime + (t - loopWallDuration) * endSpeed;
  }
  const slope = (endSpeed - startSpeed) / loopWallDuration;
  return startSpeed * t + (slope * t * t) / 2;
}

/**
 * Wall-time at which `songTime` is reached. Inverse of `songTimeAt`
 * across all three regimes (lead-in, ramp, post-ramp), so the chart
 * clock and any caller scheduling against a future song-beat agree.
 */
export function wallTimeAt(clock: LoopSpeedClock, songTime: number): number {
  const { startSpeed, endSpeed, loopWallDuration } = clock;
  if (songTime <= 0 || startSpeed === endSpeed) {
    return songTime / startSpeed;
  }
  const rampSongTime = (loopWallDuration * (startSpeed + endSpeed)) / 2;
  if (songTime >= rampSongTime) {
    return loopWallDuration + (songTime - rampSongTime) / endSpeed;
  }
  // Solve startSpeed*t + slope*t²/2 = songTime for t (positive root).
  const slope = (endSpeed - startSpeed) / loopWallDuration;
  const discriminant = startSpeed * startSpeed + 2 * slope * songTime;
  return (-startSpeed + Math.sqrt(discriminant)) / slope;
}

/**
 * Instantaneous playback rate at wall-time offset `t`. Pinned to
 * `startSpeed` during lead-in and `endSpeed` past the loop boundary,
 * mirroring the `linearRampToValueAtTime` schedule on the audio source.
 */
export function speedAt(clock: LoopSpeedClock, t: number): number {
  const { startSpeed, endSpeed, loopWallDuration } = clock;
  if (startSpeed === endSpeed || t <= 0) return startSpeed;
  if (t >= loopWallDuration) return endSpeed;
  return startSpeed + ((endSpeed - startSpeed) * t) / loopWallDuration;
}
